export const normalizeBasePath = (basePath?: string) => {
  if (!basePath) {
    return '';
  }
  const normalized = `/${basePath.replace(/^\/+|\/+$/g, '')}`;
  return normalized === '/' ? '' : normalized;
};

let shellBasePath = '';
let shellHrefResolver: ((path: string) => string) | undefined;

/**
 * Records how the app is mounted in the Forge shell. The shell can own several
 * paths for one remote, so it also supplies a resolver that maps an app path to
 * the URL it is served at; without one, the base path is prefixed as before.
 */
export const registerShellBasePath = (
  basePath?: string,
  createHref?: (path: string) => string
) => {
  const previousBasePath = shellBasePath;
  const previousResolver = shellHrefResolver;
  shellBasePath = normalizeBasePath(basePath);
  shellHrefResolver = createHref;
  return () => {
    shellBasePath = previousBasePath;
    shellHrefResolver = previousResolver;
  };
};

export const getShellBasePath = () => shellBasePath;

/**
 * The URL path an app path is served at. Standalone (wandb.ai) there is no
 * shell registered, so the path is returned untouched. Paths are always app
 * paths, including ones that start with the base path — `/wandb/foo` is the
 * `wandb` entity's `foo` project, served at `/wandb/wandb/foo`.
 */
export const shellPrefixed = (path: string) => {
  if (shellHrefResolver) {
    return shellHrefResolver(path);
  }
  return shellBasePath ? joinPath(shellBasePath, path) : path;
};

export const parsesAsPathSuffix = (origin: string, path: string): boolean => {
  try {
    // Prefixing can make markdown junk or data:/tel: hrefs parse, so check the
    // unprefixed path to preserve the fallback that returns it untouched.
    new URL(origin + path);
    return true;
  } catch {
    return false;
  }
};

export const hasPathPrefix = (path: string, prefix: string) =>
  path === prefix || path.startsWith(`${prefix}/`);

export const joinPath = (prefix: string, path: string) =>
  `${prefix}${path.startsWith('/') ? path : `/${path}`}`;
